'use client'

import { useNotifications } from "@/context/NotificationsContext";

import {
	Bell,
	ChevronsUpDown,
	Settings,
	LogOut,
	Languages,
	Moon,
	Monitor,
	Sun,
	Check,
	Search,
	LifeBuoy
} from "lucide-react"

import axiosInstance from "@/lib/axiosInstance";

import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@/components/ui/avatar"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuPortal,
} from "@/components/ui/dropdown-menu"
import {
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar"

import { useTranslation } from "react-i18next"
import { useTheme } from "@/components/theme-provider"
import { useEffect, useState } from "react"

import { useAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearch } from "@/context/SearchContext";
import { cn } from "@/lib/utils";
import SupportModal from "@/components/SupportModal";

export function NavUser() {
	const { user, isLoading, logout } = useAuth();
	const { isMobile } = useSidebar();
	const { theme, setTheme } = useTheme();
	const { setOpen } = useSearch();
	const { t, i18n } = useTranslation();
	const { openNotifications, unreadCount } = useNotifications();
	const languages = i18n.options.supportedLngs || i18n.languages || [];
	const availableLanguages = languages.filter(lang => lang !== 'cimode')
	const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');

	const getSearchIcon = () => {
		if (navigator.userAgent.indexOf('Windows') !== -1) {
			return 'CTRL';
		} else if (navigator.userAgent.indexOf('Mac') !== -1) {
			return '⌘';
		}
		return 'unknown';
	}

	function getFlagEmoji(countryCode: string) {
		if (countryCode == 'en') {
			return '🇬🇧';
		}

		const codePoints = countryCode
			.toUpperCase()
			.split('')
			.map(char => 127397 + char.charCodeAt(0));
		return String.fromCodePoint(...codePoints);
	}

	useEffect(() => {
		setCurrentLanguage(i18n.language);
	}, [i18n.language]);

	const changeLanguage = async (lng: string) => {
		i18n.changeLanguage(lng)
		setCurrentLanguage(lng);
		const response = await axiosInstance.post('/user/update', {
			username: user?.username,
			flight_tracking: user?.settings?.flight_tracking,
			locale: lng
		});
	}

	return (
		<SidebarMenu>
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							size="lg"
							className="relative data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
						>
							{
								isLoading ? (
									<>
										<Skeleton className="h-8 w-8 rounded-lg" />
										<div className="grid flex-1 gap-1 text-left text-sm leading-tight">
											<Skeleton className="h-4 w-20" />
											<Skeleton className="h-3 w-24" />
										</div>
									</>
								) : (
									<>
										<Avatar className="relative h-8 w-8 rounded-lg overflow-visible">
											{unreadCount > 0 && (
												<span className="z-50 absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
													{unreadCount > 9 ? '9+' : unreadCount}
												</span>
											)}
											<AvatarImage src={user?.avatar} alt={user?.name} />
											<AvatarFallback className="rounded-lg">
												{user?.name.split(' ').map((name) => name.charAt(0)).join('')}
											</AvatarFallback>
										</Avatar>
										<div className="grid flex-1 text-left text-sm leading-tight">
											<span className="truncate font-semibold">{user?.name}</span>
											<span className="truncate font-light text-xs">{user?.username}</span>
										</div>
									</>
								)
							}
							<ChevronsUpDown className="ml-auto size-4" />
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
						side={isMobile ? "bottom" : "right"}
						align="end"
						sideOffset={4}
					>
						<DropdownMenuLabel className="p-0 font-normal">
							<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
								<Avatar className="h-8 w-8 rounded-lg">
									<AvatarImage src={user?.avatar} alt={user?.name} />
									<AvatarFallback className="rounded-lg">
										{user?.name.split(' ').map((name) => name.charAt(0)).join('')}
									</AvatarFallback>
								</Avatar>
								<div className="grid flex-1 text-left text-sm leading-tight">
									<span className="truncate font-semibold">{user?.name}</span>
									<span className="truncate text-xs">{user?.username}</span>
								</div>
							</div>
						</DropdownMenuLabel>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							<DropdownMenuItem onClick={() => { setOpen(true); }} className="flex items-center">
								<Search />
								{t('common.search')}
								{getSearchIcon() !== 'unknown' && (
									<kbd className="ml-auto text-xs pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
										{getSearchIcon()} {getSearchIcon() === 'CTRL' && ' + '} K
									</kbd>
								)}
							</DropdownMenuItem>
							<DropdownMenuItem onClick={() => window.location.href = "/settings"}>
								<Settings />
								{t('settings.title')}
							</DropdownMenuItem>
							<DropdownMenuItem onSelect={() => openNotifications()} className="relative">
								<Bell />
								{t('settings.notifications.title')}
								{unreadCount > 0 && (
									<span className="absolute right-2 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] text-primary-foreground">
										{unreadCount > 9 ? '9+' : unreadCount}
									</span>
								)}
							</DropdownMenuItem>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<DropdownMenuGroup>
							<DropdownMenuSub>
								<DropdownMenuSubTrigger>
									<Moon />
									<span>{t('settings.theme')}</span>
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent>
										<DropdownMenuItem onClick={() => setTheme('light')}>
											<Sun />
											<span>{t('settings.lightmode')}</span>
											{theme === 'light' && <Check className="h-4 w-4" />}
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => setTheme('dark')}>
											<Moon />
											<span>{t('settings.darkmode')}</span>
											{theme === 'dark' && <Check className="h-4 w-4" />}
										</DropdownMenuItem>
										<DropdownMenuItem onClick={() => setTheme('system')}>
											<Monitor />
											<span>{t('settings.system')}</span>
											{theme === 'system' && <Check className="h-4 w-4" />}
										</DropdownMenuItem>
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
							<DropdownMenuSub>
								<DropdownMenuSubTrigger>
									<Languages />
									<span>{t('settings.language')}</span>
								</DropdownMenuSubTrigger>
								<DropdownMenuPortal>
									<DropdownMenuSubContent>
										{availableLanguages.map((lang) => (
											<DropdownMenuItem
												key={lang}
												onClick={() => changeLanguage(lang)}
												className="flex justify-between"
											>
												<span>{getFlagEmoji(lang)} {new Intl.DisplayNames([lang], { type: 'language' }).of(lang)}</span>
												{currentLanguage === lang && <Check className="h-4 w-4" />}
											</DropdownMenuItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuPortal>
							</DropdownMenuSub>
						</DropdownMenuGroup>
						<DropdownMenuSeparator />
						<SupportModal
							discordUrl="https://discord.gg/hBe7xpYM"
							supportEmail="info@flightdeck.me"
						>
							<DropdownMenuItem onSelect={(e) => e.preventDefault()}>
								<LifeBuoy />
								{t('navigation.support')}
							</DropdownMenuItem>
						</SupportModal>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={logout}>
							<LogOut />
							{t('auth.logout')}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		</SidebarMenu>
	)
}
